import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, X } from 'lucide-react';
import { Airplane, Gift, HouseLine, Sparkle, HeartStraight, CheckCircle } from '@phosphor-icons/react';
import AppIcon from '../components/AppIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICON = { honeymoon: [Airplane, 'sky'], house: [HouseLine, 'sage'], experience: [Sparkle, 'lavender'], charity: [HeartStraight, 'rose'] };
const money = (c, cur = 'usd') => `${cur === 'cad' ? 'CA$' : '$'}${(c / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fee = (cents) => Math.round((cents + 30) / (1 - 0.029)) - cents;

const ItemCard = ({ it, accepting, onGive, i }) => {
  const [Icon, tone] = it.kind === 'group_gift' ? [Gift, 'gold'] : (ICON[it.category] || [Sparkle, 'wine']);
  const pct = it.goal_cents ? Math.min(100, Math.round((it.raised_cents / it.goal_cents) * 100)) : null;
  return (
    <div data-testid={`gift-item-${it.id}`} className="bg-white rounded-3xl border border-[#EFE3D8] overflow-hidden flex flex-col">
      {it.photo ? <img src={it.photo} alt="" className="w-full aspect-[16/10] object-cover" /> : <div className="aspect-[16/10] bg-gradient-to-br from-[#F6EAE0] to-[#FBF7F2] flex items-center justify-center"><AppIcon icon={Icon} tone={tone} size="lg" reveal delay={i * 80} /></div>}
      <div className="p-5 flex-1 flex flex-col">
        <p className="text-[10px] uppercase tracking-wider text-[#7B2C3B]">{it.kind === 'group_gift' ? 'Group gift' : 'Fund'}</p>
        <h3 className="font-serif text-2xl text-[#2A2A2A] mt-1">{it.title}</h3>
        {it.description && <p className="text-sm text-[#6A6A6A] mt-1">{it.description}</p>}
        {pct !== null && (
          <div className="mt-4">
            <div className="h-2 rounded-full bg-[#F4E4DA] overflow-hidden"><div className="h-full rounded-full bg-[#7B2C3B] transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
            <p data-testid={`gift-progress-${it.id}`} className="text-xs text-[#6A6A6A] mt-1.5">{money(it.raised_cents, it.currency)} of {money(it.goal_cents, it.currency)} · {it.contributors} contributor{it.contributors === 1 ? '' : 's'}{pct >= 100 ? ' · Fully funded, extra goes to the couple' : ''}</p>
          </div>
        )}
        <button data-testid={`gift-give-${it.id}`} disabled={!accepting} onClick={() => onGive(it)} className="mt-5 w-full py-3.5 rounded-full bg-[#7B2C3B] text-[#FBF7F2] font-medium active:scale-[0.98] transition-transform disabled:opacity-40">
          {accepting ? (it.kind === 'group_gift' ? 'Chip in' : 'Contribute') : 'Card gifts opening soon'}
        </button>
      </div>
    </div>
  );
};

const GiveDialog = ({ it, slug, onClose }) => {
  const [f, setF] = useState({ amount: 50, name: '', email: '', note: '', cover_fee: true });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const upd = (patch) => { setErr(''); setF((prev) => ({ ...prev, ...patch })); };
  const cents = Math.round((parseFloat(f.amount) || 0) * 100);
  const total = cents + (f.cover_fee ? fee(cents) : 0);
  const pay = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      const { data } = await axios.post(`${API}/gifts/public/${slug}/checkout`, { ...f, amount: parseFloat(f.amount), item_id: it.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e2) { setErr(typeof e2?.response?.data?.detail === 'string' ? e2.response.data.detail : 'Please check the amount (at least $5) and your name.'); setBusy(false); }
  };
  const cls = 'mt-1 w-full px-4 py-3 rounded-xl border border-[#EFE3D8] bg-white text-base focus:outline-none focus:border-[#7B2C3B]';
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <form data-testid="gift-dialog" onSubmit={pay} onClick={(e) => e.stopPropagation()} className="bg-[#FBF7F2] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-3 max-h-[92vh] overflow-auto">
        <div className="flex items-start justify-between"><h3 className="font-serif text-2xl">{it.title}</h3><button type="button" onClick={onClose} aria-label="Close"><X className="w-5 h-5" /></button></div>
        <div className="flex gap-2">{[25, 50, 100, 250].map((a) => <button type="button" key={a} data-testid={`gift-amount-${a}`} onClick={() => upd({ amount: a })} className={`flex-1 py-2.5 rounded-full border text-sm ${Number(f.amount) === a ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8]'}`}>${a}</button>)}</div>
        <label className="block text-xs uppercase tracking-wide text-[#6A6A6A]">Amount<input data-testid="gift-amount-input" type="number" min="5" step="1" required value={f.amount} onChange={(e) => upd({ amount: e.target.value })} className={cls} /></label>
        <label className="block text-xs uppercase tracking-wide text-[#6A6A6A]">Your name<input data-testid="gift-name-input" required value={f.name} onChange={(e) => upd({ name: e.target.value })} className={cls} /></label>
        <label className="block text-xs uppercase tracking-wide text-[#6A6A6A]">Email for your receipt (optional)<input data-testid="gift-email-input" type="email" value={f.email} onChange={(e) => upd({ email: e.target.value })} className={cls} /></label>
        <label className="block text-xs uppercase tracking-wide text-[#6A6A6A]">Note to the couple (optional)<textarea data-testid="gift-note-input" rows={2} value={f.note} onChange={(e) => upd({ note: e.target.value })} className={cls} /></label>
        <label className="flex items-center gap-2 text-sm"><input data-testid="gift-cover-fee" type="checkbox" checked={f.cover_fee} onChange={(e) => upd({ cover_fee: e.target.checked })} className="w-5 h-5 accent-[#7B2C3B]" /> Cover the card fee ({money(fee(cents))}) so they get the full gift</label>
        {err && <p data-testid="gift-error" className="text-sm text-[#7B2C3B]">{err}</p>}
        <button data-testid="gift-pay-btn" disabled={busy || cents < 500} className="w-full py-4 rounded-full bg-[#7B2C3B] text-[#FBF7F2] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-40">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Pay {money(total)} by card
        </button>
        <p className="text-[11px] text-center text-[#9A8A7E]">Secure checkout by Stripe. Your Wedding Friends takes no fee. Gifts can&apos;t be refunded in the app.</p>
      </form>
    </div>
  );
};

export default function GiftPage() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [giving, setGiving] = useState(null);
  const [thanks, setThanks] = useState(null);

  useEffect(() => { axios.get(`${API}/gifts/public/${slug}`).then(({ data }) => setD(data)).catch(() => setErr('This gift page is not available.')); }, [slug, thanks]);
  useEffect(() => {
    const sid = sp.get('session_id');
    if (!sid) return undefined;
    let n = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/gifts/public/status/${sid}`);
        if (data.status === 'paid') { setThanks(data); return; }
      } catch { return; }
      if (++n < 10) setTimeout(poll, 2000); else setThanks({ pending: true });
    };
    poll();
    return undefined;
  }, [sp]);

  return (
    <div className="min-h-screen bg-[#FBF7F2] px-5 py-12">
      <div className="max-w-5xl mx-auto">
        {err && <p data-testid="gift-page-error" className="text-center text-[#7B2C3B] py-20">{err}</p>}
        {!d && !err && <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#7B2C3B]" /></div>}
        {thanks && (
          <div data-testid="gift-thanks" className="max-w-md mx-auto mb-10 rounded-3xl bg-white border border-[#C8E6C9] p-6 text-center">
            <AppIcon icon={CheckCircle} tone="sage" size="lg" reveal className="mx-auto" />
            <p className="font-serif text-2xl mt-3">{thanks.pending ? 'Payment processing' : `Thank you, ${thanks.name}!`}</p>
            <p className="text-sm text-[#6A6A6A] mt-1">{thanks.pending ? 'We will update the page once Stripe confirms.' : `Your gift toward ${thanks.item_title} is on its way to the couple.`}</p>
          </div>
        )}
        {d && (
          <>
            <div className="text-center max-w-xl mx-auto mb-10">
              <p className="section-eyebrow">Gifts for</p>
              <h1 className="font-serif text-4xl sm:text-5xl text-[#2A2A2A] mt-2">{d.couple}</h1>
              {d.intro && <p className="text-[#6A6A6A] mt-3">{d.intro}</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {d.items.map((it, i) => <ItemCard key={it.id} it={it} i={i} accepting={d.accepting} onGive={setGiving} />)}
            </div>
            {!d.items.length && <p className="text-center text-[#9A8A7E]">No funds or group gifts yet.</p>}
          </>
        )}
      </div>
      {giving && <GiveDialog it={giving} slug={slug} onClose={() => setGiving(null)} />}
    </div>
  );
}
